"use client";

import { useRef, useState, useEffect } from "react";
import Link from "next/link";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValueEvent,
  type MotionValue,
} from "framer-motion";

/* ------------------------------------------------------------------ */
/*  "Explosion Outward" scroll animation                               */
/*  Phone stays centered, elements burst outward at each phase         */
/* ------------------------------------------------------------------ */

/** Viewport-aware burst multiplier: returns a scale factor 0-1 */
function useViewportScale() {
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      // At 1200px+ use full values. At 375px use ~0.35
      setScale(Math.min(1, Math.max(0.32, (w - 160) / 1040)));
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return scale;
}

/** Clamped interpolation - prevents framer-motion's default extrapolation */
function useClampedTransform(
  mv: MotionValue<number>,
  input: number[],
  output: number[]
) {
  return useTransform(mv, (v: number) => {
    if (v <= input[0]) return output[0];
    if (v >= input[input.length - 1]) return output[output.length - 1];
    for (let i = 0; i < input.length - 1; i++) {
      if (v >= input[i] && v <= input[i + 1]) {
        const t = (v - input[i]) / (input[i + 1] - input[i]);
        return output[i] + t * (output[i + 1] - output[i]);
      }
    }
    return output[output.length - 1];
  });
}

/** Helper: clamp-based opacity for a scroll range */
function usePhaseOpacity(
  progress: MotionValue<number>,
  fadeIn: number,
  fullStart: number,
  fullEnd: number,
  fadeOut: number
) {
  return useClampedTransform(progress, [fadeIn, fullStart, fullEnd, fadeOut], [0, 1, 1, 0]);
}

/* ── Floating label that bursts from the phone ────────────────────── */
function BurstLabel({
  children,
  progress,
  appear,
  x,
  y,
  delay,
  vScale,
}: {
  children: React.ReactNode;
  progress: MotionValue<number>;
  appear: number;
  x: number;
  y: number;
  delay: number;
  vScale: number;
}) {
  // Scale offset with viewport, with a floor that clears the phone edge
  const clampedScale = Math.max(0.38, vScale);
  const sx = x * clampedScale;
  const sy = y * clampedScale;
  const opacity = useClampedTransform(progress, [appear, appear + 0.015, 0.32, 0.36], [0, 1, 1, 0]);
  const translateX = useClampedTransform(progress, [appear, appear + 0.03], [0, sx]);
  const translateY = useClampedTransform(progress, [appear, appear + 0.03], [0, sy]);
  const scale = useClampedTransform(progress, [appear, appear + 0.02], [0.2, 1]);

  return (
    <motion.div
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        opacity,
        x: translateX,
        y: translateY,
        scale,
        zIndex: 10,
      }}
    >
      <div
        style={{
          background: "rgba(255,255,255,0.97)",
          backdropFilter: "blur(16px)",
          border: "1px solid rgba(44,37,32,0.06)",
          borderRadius: 12,
          padding: "7px 16px",
          fontSize: vScale < 0.5 ? 11 : 13,
          fontWeight: 600,
          color: "#2C2520",
          whiteSpace: "nowrap",
          boxShadow: "0 4px 24px rgba(44,37,32,0.12), 0 1px 3px rgba(44,37,32,0.06)",
        }}
      >
        {children}
      </div>
    </motion.div>
  );
}

/* ── Floating photo that bursts from the phone ────────────────────── */
function BurstPhoto({
  src,
  label,
  progress,
  appear,
  x,
  y,
  rotate,
  vScale,
}: {
  src: string;
  label: string;
  progress: MotionValue<number>;
  appear: number;
  x: number;
  y: number;
  rotate: number;
  vScale: number;
}) {
  const clampedScale = Math.max(0.38, vScale);
  const sx = x * clampedScale;
  const sy = y * clampedScale;
  const photoSize = vScale < 0.5 ? 110 : vScale < 0.7 ? 160 : 210;

  const opacity = useClampedTransform(progress, [appear, appear + 0.03, 0.50, 0.56], [0, 1, 1, 0]);
  const translateX = useClampedTransform(progress, [appear, appear + 0.05, 0.50, 0.56], [0, sx, sx, sx * 0.4]);
  const translateY = useClampedTransform(progress, [appear, appear + 0.05, 0.50, 0.56], [0, sy, sy, sy * 0.4]);
  const scale = useClampedTransform(progress, [appear, appear + 0.04, 0.50, 0.56], [0.15, 1, 1, 0.5]);
  const rotateZ = useClampedTransform(progress, [appear, appear + 0.05], [0, rotate]);

  return (
    <motion.div
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        opacity,
        x: translateX,
        y: translateY,
        scale,
        rotateZ,
        zIndex: 8,
      }}
    >
      <div
        style={{
          width: photoSize,
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 16px 48px rgba(44,37,32,0.22), 0 4px 12px rgba(44,37,32,0.08)",
          background: "#fff",
        }}
      >
        <img
          src={src}
          alt=""
          style={{
            width: "100%",
            aspectRatio: "3/4",
            objectFit: "cover",
            display: "block",
          }}
        />
        <div
          style={{
            padding: "6px 10px",
            fontSize: 10,
            fontWeight: 700,
            color: "#D4613A",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          {label}
        </div>
      </div>
    </motion.div>
  );
}

/* ── Floating hashtag pill ────────────────────────────────────────── */
function BurstTag({
  children,
  progress,
  appear,
  x,
  y,
  vScale,
}: {
  children: React.ReactNode;
  progress: MotionValue<number>;
  appear: number;
  x: number;
  y: number;
  vScale: number;
}) {
  const clampedScale = Math.max(0.38, vScale);
  const sx = x * clampedScale;
  const sy = y * clampedScale;
  const opacity = useClampedTransform(progress, [appear, appear + 0.02, 0.72, 0.78], [0, 1, 1, 0]);
  const translateX = useClampedTransform(progress, [appear, appear + 0.035], [0, sx]);
  const translateY = useClampedTransform(progress, [appear, appear + 0.035], [0, sy]);
  const scale = useClampedTransform(progress, [appear, appear + 0.02], [0.3, 1]);

  return (
    <motion.div
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        opacity,
        x: translateX,
        y: translateY,
        scale,
        zIndex: 10,
      }}
    >
      <div
        style={{
          background: "#FDF6EE",
          border: "1px solid #E8DDD1",
          borderRadius: 99,
          padding: "5px 14px",
          fontSize: vScale < 0.5 ? 11 : 13,
          fontWeight: 600,
          color: "#2C2520",
          whiteSpace: "nowrap",
          boxShadow: "0 2px 12px rgba(44,37,32,0.06)",
        }}
      >
        {children}
      </div>
    </motion.div>
  );
}

/* ── Main component ───────────────────────────────────────────────── */
export default function ScrollTransformation() {
  const sectionRef = useRef<HTMLElement>(null);
  const vScale = useViewportScale();
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  // Shorthand for clamped transforms bound to scrollYProgress
  const ct = (input: number[], output: number[]) =>
    useClampedTransform(scrollYProgress, input, output);

  // ── Cinematic background transitions ─────────────────────────────
  const bgVignetteOpacity = ct([0.08, 0.15, 0.30, 0.36], [0, 0.6, 0.6, 0]);
  const bgDarkenOpacity = ct([0.10, 0.16, 0.30, 0.36], [0, 0.18, 0.18, 0]);
  const bgWarmPulse = ct([0.34, 0.40, 0.52, 0.58], [0, 0.45, 0.45, 0]);

  // ── Phone internal states ──────────────────────────────────────
  const badPhotoOpacity = ct([0.0, 0.05, 0.28, 0.34], [0, 1, 1, 0]);
  const scanLineTop = ct([0.15, 0.30], [40, 540]);
  const scanLineOpacity = ct([0.14, 0.16, 0.30, 0.32], [0, 1, 1, 0]);

  // Phone grid (inside phone) - appears after scan completes
  const phoneGridOpacity = ct([0.34, 0.40], [0, 1]);

  // Title + desc inside phone
  const phoneTitleOpacity = ct([0.50, 0.54], [0, 1]);
  const phoneTitleY = ct([0.50, 0.56], [14, 0]);
  const phoneDescOpacity = ct([0.54, 0.58], [0, 1]);

  // Tags + price inside phone
  const phoneTagsOpacity = ct([0.58, 0.62], [0, 1]);
  const phonePriceOpacity = ct([0.64, 0.70], [0, 1]);
  const phonePriceY = ct([0.64, 0.70], [10, 0]);

  // CTA button
  const ctaOpacity = ct([0.80, 0.86], [0, 1]);
  const ctaY = ct([0.80, 0.86], [50, 0]);
  const ctaScale = ct([0.80, 0.90], [0.88, 1]);

  // Phone wrapper
  const phoneWrapOpacity = ct([0.0, 0.04], [0, 1]);
  const phoneScale = ct([0.0, 0.06, 0.78, 0.86], [0.90, 1, 1, 0.90]);

  // Phone glow during scan - DRAMATIC pulsing
  const glowOpacity = ct([0.12, 0.17, 0.28, 0.32], [0, 0.9, 0.9, 0]);
  const glowScale = ct([0.12, 0.22, 0.28, 0.32], [0.7, 1.4, 1.5, 1.0]);

  // Second glow pulse during photo burst - explosive
  const burstGlowOpacity = ct([0.34, 0.38, 0.46, 0.52], [0, 0.7, 0.7, 0]);

  // Intro text - must vanish well before scan at 0.15
  const introOpacity = ct([0.0, 0.01, 0.04, 0.08], [1, 1, 0, 0]);
  const introScale = ct([0.0, 0.08], [1, 0.90]);

  // Timer
  const timerOpacity = ct([0.04, 0.08, 0.80, 0.84], [0, 1, 1, 0]);

  // Floating text card (left side) - hidden on small screens
  const textCardOpacity = ct([0.52, 0.57, 0.72, 0.78], [0, 1, 1, 0]);
  const textCardX = ct([0.52, 0.58], [60, 0]);

  // Platform logos (right side) - hidden on small screens
  const platformOpacity = ct([0.58, 0.62, 0.72, 0.78], [0, 1, 1, 0]);
  const platformX = ct([0.58, 0.62], [-40, 0]);

  // Final result glow - warm crescendo
  const finalGlowOpacity = ct([0.80, 0.94], [0, 0.55]);

  // Expanding ring during scan (decorative)
  const ringOpacity = ct([0.12, 0.17, 0.30, 0.34], [0, 0.3, 0.3, 0]);
  const ringScale = ct([0.12, 0.30], [0.5, 3.0]);

  // Second decorative ring (staggered)
  const ring2Opacity = ct([0.16, 0.21, 0.30, 0.34], [0, 0.15, 0.15, 0]);
  const ring2Scale = ct([0.16, 0.30], [0.3, 2.0]);

  // Dynamic phone shadow
  const phoneShadowIntensity = ct([0.0, 0.12, 0.30, 0.36], [0.12, 0.35, 0.35, 0.15]);

  // Timer text
  const timerRef = useRef<HTMLDivElement>(null);
  useMotionValueEvent(scrollYProgress, "change", (v: number) => {
    if (timerRef.current) {
      const totalSec = v * 30;
      const m = Math.floor(totalSec / 60);
      const s = Math.floor(totalSec % 60);
      timerRef.current.textContent = `${m}:${s < 10 ? "0" : ""}${s}`;
    }
  });

  return (
    <section
      ref={sectionRef}
      id="demo"
      style={{ height: "500vh", position: "relative" }}
    >
      <div
        style={{
          position: "sticky",
          top: 0,
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          clipPath: "inset(0)",
        }}
      >
        {/* ── Cinematic background layers ──────────────────────── */}

        {/* Dark vignette during scan phase */}
        <motion.div
          style={{
            position: "absolute",
            inset: 0,
            background: "radial-gradient(ellipse at center, transparent 30%, rgba(44,37,32,0.3) 100%)",
            opacity: bgVignetteOpacity,
            zIndex: 1,
            pointerEvents: "none",
          }}
        />

        {/* Overall darken during AI analysis */}
        <motion.div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(44,37,32,1)",
            opacity: bgDarkenOpacity,
            zIndex: 1,
            pointerEvents: "none",
          }}
        />

        {/* Warm radial pulse during photo generation */}
        <motion.div
          style={{
            position: "absolute",
            inset: 0,
            background: "radial-gradient(ellipse at center, rgba(212,97,58,0.15) 0%, rgba(212,97,58,0.05) 40%, transparent 70%)",
            opacity: bgWarmPulse,
            zIndex: 1,
            pointerEvents: "none",
          }}
        />

        {/* ── Intro text ─────────────────────────────────────────── */}
        <motion.div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            zIndex: 4,
            opacity: introOpacity,
            scale: introScale,
            pointerEvents: "none",
          }}
        >
          <h2
            style={{
              fontFamily: "var(--font-newsreader), Georgia, serif",
              fontStyle: "italic",
              fontSize: "clamp(36px, 5vw, 56px)",
              marginBottom: 12,
            }}
          >
            Et si on s&apos;en occupait ?
          </h2>
          <p style={{ fontSize: 20, color: "rgba(44,37,32,0.6)" }}>
            Regarde ce qu&apos;on en fait.
          </p>
        </motion.div>

        {/* ── Final background glow (grand finale) ────────────────── */}
        <motion.div
          style={{
            position: "absolute",
            inset: 0,
            background: "radial-gradient(ellipse at center, rgba(212,97,58,0.25) 0%, rgba(212,97,58,0.08) 50%, transparent 80%)",
            opacity: finalGlowOpacity,
            zIndex: 0,
          }}
        />

        {/* ── Burst labels (Phase 2: AI Analysis) ────────────────── */}
        <BurstLabel progress={scrollYProgress} appear={0.17} x={-480} y={-220} delay={0} vScale={vScale}>
          <span style={{ color: "#D4613A", marginRight: 6 }}>&#9679;</span>
          Cuir v{"\u00E9"}ritable
        </BurstLabel>
        <BurstLabel progress={scrollYProgress} appear={0.19} x={440} y={-180} delay={80} vScale={vScale}>
          <span style={{ color: "#3A7D5C", marginRight: 6 }}>&#9679;</span>
          Marque : S{"\u00E9"}zane
        </BurstLabel>
        <BurstLabel progress={scrollYProgress} appear={0.21} x={-460} y={-20} delay={160} vScale={vScale}>
          <span style={{ color: "#D4613A", marginRight: 6 }}>&#9679;</span>
          Taille : M
        </BurstLabel>
        <BurstLabel progress={scrollYProgress} appear={0.23} x={460} y={60} delay={240} vScale={vScale}>
          <span style={{ color: "#3A7D5C", marginRight: 6 }}>&#9679;</span>
          {"\u00C9"}tat : Tr{"\u00E8"}s bon
        </BurstLabel>
        <BurstLabel progress={scrollYProgress} appear={0.25} x={-440} y={220} delay={320} vScale={vScale}>
          <span style={{ color: "#D4613A", marginRight: 6 }}>&#9679;</span>
          Coupe droite
        </BurstLabel>

        {/* ── Burst photos (Phase 3: Photo Generation) ───────────── */}
        <BurstPhoto
          src="/marketing/jeans-good-1.webp"
          label="Studio"
          progress={scrollYProgress}
          appear={0.36}
          x={-520}
          y={-140}
          rotate={-6}
          vScale={vScale}
        />
        <BurstPhoto
          src="/marketing/jeans-good-2.webp"
          label="Stylis{'\u00E9'}"
          progress={scrollYProgress}
          appear={0.38}
          x={500}
          y={-80}
          rotate={5}
          vScale={vScale}
        />
        <BurstPhoto
          src="/marketing/jeans-good-3.webp"
          label="R{'\u00E9'}el"
          progress={scrollYProgress}
          appear={0.40}
          x={-480}
          y={150}
          rotate={4}
          vScale={vScale}
        />
        <BurstPhoto
          src="/marketing/jeans-good-4.webp"
          label="D{'\u00E9'}tail"
          progress={scrollYProgress}
          appear={0.42}
          x={480}
          y={200}
          rotate={-5}
          vScale={vScale}
        />

        {/* ── Burst hashtags (Phase 4-5) ─────────────────────────── */}
        <BurstTag progress={scrollYProgress} appear={0.61} x={460} y={-180} vScale={vScale}>
          #denim
        </BurstTag>
        <BurstTag progress={scrollYProgress} appear={0.615} x={-480} y={-100} vScale={vScale}>
          #zara
        </BurstTag>
        <BurstTag progress={scrollYProgress} appear={0.62} x={500} y={-20} vScale={vScale}>
          #slim
        </BurstTag>
        <BurstTag progress={scrollYProgress} appear={0.625} x={-440} y={80} vScale={vScale}>
          #bleu
        </BurstTag>
        <BurstTag progress={scrollYProgress} appear={0.63} x={480} y={160} vScale={vScale}>
          #vintage
        </BurstTag>

        {/* ── Floating text card (left, Phase 4) - hidden on mobile ── */}
        <motion.div
          style={{
            position: "absolute",
            left: "4vw",
            top: "50%",
            y: "-50%",
            opacity: textCardOpacity,
            x: textCardX,
            zIndex: 10,
            maxWidth: 320,
            display: vScale < 0.45 ? "none" : "block",
          }}
        >
          <div
            style={{
              background: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(16px)",
              borderRadius: 16,
              padding: "24px",
              boxShadow: "0 8px 40px rgba(44,37,32,0.1)",
              border: "1px solid rgba(44,37,32,0.06)",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#D4613A",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                marginBottom: 8,
              }}
            >
              Titre g{"\u00E9"}n{"\u00E9"}r{"\u00E9"}
            </div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: "#2C2520",
                lineHeight: 1.3,
                marginBottom: 12,
              }}
            >
              Jean Slim Zara Bleu Brut T38
            </div>
            <div
              style={{
                fontSize: 13,
                color: "rgba(44,37,32,0.6)",
                lineHeight: 1.6,
              }}
            >
              Jean slim coupe ajust{"\u00E9"}e, denim brut japonais. Port{"\u00E9"}{" "}
              deux fois, comme neuf. Taille 38, longueur 32.
            </div>
          </div>
        </motion.div>

        {/* ── Platform compatibility (right, Phase 4-5) - hidden on mobile ── */}
        <motion.div
          style={{
            position: "absolute",
            right: "4vw",
            bottom: "18vh",
            opacity: platformOpacity,
            x: platformX,
            zIndex: 10,
            display: vScale < 0.45 ? "none" : "block",
          }}
        >
          <div
            style={{
              background: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(16px)",
              borderRadius: 16,
              padding: "20px 24px",
              boxShadow: "0 8px 40px rgba(44,37,32,0.1)",
              border: "1px solid rgba(44,37,32,0.06)",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#3A7D5C",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                marginBottom: 12,
              }}
            >
              Pr{"\u00EA"}t {"\u00E0"} publier
            </div>
            {["Vinted", "Depop", "Leboncoin", "Instagram"].map((p) => (
              <div
                key={p}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "6px 0",
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#2C2520",
                }}
              >
                <span style={{ color: "#3A7D5C", fontSize: 16 }}>{"\u2713"}</span>
                {p}
              </div>
            ))}
          </div>
        </motion.div>

        {/* ── Timer ──────────────────────────────────────────────── */}
        <motion.div
          ref={timerRef}
          style={{
            position: "absolute",
            top: "8vh",
            left: "50%",
            x: "-50%",
            fontFamily: "var(--font-manrope), system-ui, sans-serif",
            fontSize: 20,
            fontWeight: 300,
            color: "#D4613A",
            letterSpacing: 2,
            opacity: timerOpacity,
            zIndex: 12,
          }}
        >
          0:00
        </motion.div>

        {/* ── Phone ──────────────────────────────────────────────── */}
        <motion.div style={{
          position: "relative",
          zIndex: 9,
          opacity: phoneWrapOpacity,
          scale: phoneScale,
        }}>
          {/* Expanding decorative ring during scan */}
          <motion.div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              width: 300,
              height: 300,
              marginTop: -150,
              marginLeft: -150,
              borderRadius: "50%",
              border: "1.5px solid rgba(212,97,58,0.18)",
              opacity: ringOpacity,
              scale: ringScale,
              zIndex: -1,
              pointerEvents: "none",
            }}
          />
          {/* Second staggered ring */}
          <motion.div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              width: 300,
              height: 300,
              marginTop: -150,
              marginLeft: -150,
              borderRadius: "50%",
              border: "1px solid rgba(212,97,58,0.10)",
              opacity: ring2Opacity,
              scale: ring2Scale,
              zIndex: -1,
              pointerEvents: "none",
            }}
          />

          {/* Glow ring during scan - DRAMATIC */}
          <motion.div
            style={{
              position: "absolute",
              inset: -50,
              borderRadius: 60,
              background: "radial-gradient(ellipse, rgba(212,97,58,0.45) 0%, rgba(212,97,58,0.15) 40%, transparent 70%)",
              opacity: glowOpacity,
              scale: glowScale,
              zIndex: -1,
              pointerEvents: "none",
            }}
          />

          {/* Second glow pulse during photo burst */}
          <motion.div
            style={{
              position: "absolute",
              inset: -60,
              borderRadius: 60,
              background: "radial-gradient(ellipse, rgba(212,97,58,0.25) 0%, rgba(253,246,238,0.15) 50%, transparent 70%)",
              opacity: burstGlowOpacity,
              zIndex: -1,
              pointerEvents: "none",
            }}
          />

          <motion.div
            style={{
              width: 300,
              height: 600,
              background: "#fff",
              borderRadius: 36,
              border: "2px solid rgba(44,37,32,0.12)",
              overflow: "hidden",
              position: "relative",
              boxShadow: useTransform(
                phoneShadowIntensity,
                (v) => `0 ${20 + v * 60}px ${40 + v * 100}px rgba(44,37,32,${v}), 0 0 0 1px rgba(44,37,32,0.04)`
              ),
            }}
          >
            {/* Notch */}
            <div
              style={{
                position: "absolute",
                top: 0,
                left: "50%",
                transform: "translateX(-50%)",
                width: 120,
                height: 28,
                background: "#2C2520",
                borderRadius: "0 0 18px 18px",
                zIndex: 10,
              }}
            />

            {/* Phone content */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                fontSize: 12,
                overflow: "hidden",
              }}
            >
              {/* Status bar */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 20px 0",
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#2C2520",
                  flexShrink: 0,
                  height: 32,
                  zIndex: 11,
                }}
              >
                <span>9:41</span>
                <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <span style={{ fontSize: 10 }}>
                    {"\u25CF\u25CF\u25CF\u25CB"}
                  </span>
                  <span style={{
                    width: 20,
                    height: 10,
                    border: "1.5px solid #2C2520",
                    borderRadius: 3,
                    position: "relative",
                    display: "inline-block",
                  }}>
                    <span style={{
                      position: "absolute",
                      left: 1.5,
                      top: 1.5,
                      bottom: 1.5,
                      width: "60%",
                      background: "#3A7D5C",
                      borderRadius: 1,
                    }} />
                  </span>
                </span>
              </div>

              {/* App header bar (appears with grid) */}
              <motion.div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "4px 14px 6px",
                  flexShrink: 0,
                  opacity: phoneGridOpacity,
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--font-newsreader), Georgia, serif",
                    fontStyle: "italic",
                    fontSize: 15,
                    fontWeight: 500,
                    color: "#D4613A",
                    letterSpacing: "-0.02em",
                  }}
                >
                  Vitrine
                </span>
              </motion.div>

              {/* Scan line */}
              <motion.div
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  height: 2,
                  background: "linear-gradient(90deg, transparent 0%, #D4613A 15%, #FF8A5C 35%, #fff 50%, #FF8A5C 65%, #D4613A 85%, transparent 100%)",
                  boxShadow: "0 0 40px 10px rgba(212,97,58,0.6), 0 0 80px 6px rgba(212,97,58,0.3)",
                  top: scanLineTop,
                  opacity: scanLineOpacity,
                  zIndex: 5,
                }}
              />
              {/* Scan line ambient glow - taller, softer */}
              <motion.div
                style={{
                  position: "absolute",
                  left: -10,
                  right: -10,
                  height: 60,
                  background: "linear-gradient(180deg, rgba(212,97,58,0.2) 0%, rgba(212,97,58,0.05) 50%, transparent 100%)",
                  top: scanLineTop,
                  opacity: scanLineOpacity,
                  zIndex: 4,
                  filter: "blur(12px)",
                }}
              />

              {/* Photo area - fixed height container */}
              <div style={{ position: "relative", height: 200, flexShrink: 0, margin: "0 12px" }}>
                {/* Bad photo */}
                <motion.div
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: 10,
                    overflow: "hidden",
                    opacity: badPhotoOpacity,
                  }}
                >
                  <img
                    src="/marketing/jeans-bad.webp"
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      filter: "saturate(0.7) contrast(0.9) brightness(0.95)",
                    }}
                  />
                  {/* Amateur photo overlay */}
                  <div style={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(145deg, rgba(255,200,50,0.08) 0%, transparent 50%, rgba(0,0,0,0.12) 100%)",
                    pointerEvents: "none",
                  }} />
                  {/* Timestamp watermark */}
                  <div style={{
                    position: "absolute",
                    bottom: 8,
                    right: 8,
                    fontSize: 8,
                    color: "rgba(255,140,0,0.7)",
                    fontFamily: "monospace",
                    fontWeight: 700,
                  }}>
                    05/04/2026 14:32
                  </div>
                </motion.div>

                {/* Photo grid (replaces bad photo) */}
                <motion.div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gridTemplateRows: "1fr 1fr",
                    gap: 3,
                    opacity: phoneGridOpacity,
                  }}
                >
                  {[1, 2, 3, 4].map((n) => (
                    <div
                      key={n}
                      style={{
                        borderRadius: 8,
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <img
                        src={`/marketing/jeans-good-${n}.webp`}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      {n === 1 && (
                        <div style={{
                          position: "absolute",
                          bottom: 4,
                          left: 4,
                          background: "rgba(0,0,0,0.55)",
                          color: "#fff",
                          fontSize: 7,
                          fontWeight: 700,
                          padding: "2px 5px",
                          borderRadius: 4,
                          letterSpacing: "0.04em",
                        }}>
                          1/4
                        </div>
                      )}
                    </div>
                  ))}
                </motion.div>
              </div>

              {/* Listing details area */}
              <div style={{ padding: "0 14px", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                {/* Title */}
                <motion.div
                  style={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: "#2C2520",
                    marginTop: 10,
                    lineHeight: 1.2,
                    opacity: phoneTitleOpacity,
                    y: phoneTitleY,
                  }}
                >
                  Jean Slim Zara Bleu Brut T38
                </motion.div>

                {/* Brand + condition badges */}
                <motion.div
                  style={{
                    display: "flex",
                    gap: 6,
                    marginTop: 6,
                    opacity: phoneTitleOpacity,
                  }}
                >
                  <span style={{
                    fontSize: 9,
                    fontWeight: 600,
                    color: "#D4613A",
                    background: "rgba(212,97,58,0.08)",
                    padding: "2px 7px",
                    borderRadius: 4,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}>
                    Zara
                  </span>
                  <span style={{
                    fontSize: 9,
                    fontWeight: 600,
                    color: "#3A7D5C",
                    background: "rgba(58,125,92,0.08)",
                    padding: "2px 7px",
                    borderRadius: 4,
                  }}>
                    Tr{"\u00E8"}s bon {"\u00E9"}tat
                  </span>
                </motion.div>

                {/* Description */}
                <motion.div
                  style={{
                    fontSize: 11,
                    color: "rgba(44,37,32,0.55)",
                    lineHeight: 1.5,
                    marginTop: 8,
                    opacity: phoneDescOpacity,
                  }}
                >
                  Jean slim coupe ajust{"\u00E9"}e, denim brut japonais.
                  Port{"\u00E9"} deux fois, comme neuf. Taille 38.
                </motion.div>

                {/* Tags */}
                <motion.div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 4,
                    marginTop: 10,
                    opacity: phoneTagsOpacity,
                  }}
                >
                  {["#denim", "#zara", "#slim", "#bleu", "#vintage"].map((tag) => (
                    <span
                      key={tag}
                      style={{
                        background: "#FDF6EE",
                        border: "1px solid #E8DDD1",
                        padding: "2px 8px",
                        borderRadius: 99,
                        fontSize: 9,
                        fontWeight: 500,
                        color: "#2C2520",
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </motion.div>

                {/* Price + publish button */}
                <motion.div
                  style={{
                    marginTop: "auto",
                    paddingBottom: 14,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    opacity: phonePriceOpacity,
                    y: phonePriceY,
                  }}
                >
                  <div>
                    <div style={{
                      fontWeight: 800,
                      fontSize: 22,
                      color: "#2C2520",
                      lineHeight: 1,
                    }}>
                      45 {"\u20AC"}
                    </div>
                    <div style={{
                      fontSize: 9,
                      color: "rgba(44,37,32,0.4)",
                      marginTop: 2,
                    }}>
                      Prix sugg{"\u00E9"}r{"\u00E9"}
                    </div>
                  </div>
                  <div style={{
                    background: "#D4613A",
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "8px 16px",
                    borderRadius: 99,
                    letterSpacing: "0.02em",
                  }}>
                    Publier
                  </div>
                </motion.div>
              </div>

              {/* Bottom home indicator */}
              <div style={{
                width: 100,
                height: 4,
                background: "rgba(44,37,32,0.2)",
                borderRadius: 99,
                margin: "0 auto 6px",
                flexShrink: 0,
              }} />
            </div>
          </motion.div>
        </motion.div>

        {/* ── Final CTA ──────────────────────────────────────────── */}
        <motion.div
          style={{
            position: "absolute",
            bottom: "6vh",
            left: "50%",
            x: "-50%",
            opacity: ctaOpacity,
            y: ctaY,
            scale: ctaScale,
            zIndex: 15,
            textAlign: "center",
            width: "max-content",
          }}
        >
          <Link
            href="/login"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              background: "linear-gradient(135deg, #D4613A 0%, #E07B52 100%)",
              color: "#FDF6EE",
              padding: "20px 52px",
              borderRadius: 99,
              fontSize: 18,
              fontWeight: 600,
              textDecoration: "none",
              boxShadow: "0 6px 32px rgba(212,97,58,0.4), 0 16px 56px rgba(212,97,58,0.18), inset 0 1px 0 rgba(255,255,255,0.15)",
              letterSpacing: "-0.01em",
            }}
          >
            Essayer avec ta photo
          </Link>
          <div
            style={{
              marginTop: 16,
              fontSize: 14,
              color: "rgba(44,37,32,0.5)",
              fontWeight: 500,
            }}
          >
            30 secondes, gratuit pendant la b{"\u00EA"}ta
          </div>
        </motion.div>
      </div>
    </section>
  );
}
